import { useState, useCallback, useMemo } from 'react';
import { DateRangePicker } from 'react-date-range';
import { useLocale, useTranslations } from 'next-intl';
import format from 'date-fns/format';
import startOfToday from 'date-fns/startOfToday';
import addYears from 'date-fns/addYears';
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import differenceInDays from 'date-fns/differenceInDays';
import { useMediaQuery } from 'react-responsive';
import { InexactDaysButtons } from './InexactDaysButtons';
import { Day } from './Day';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { sv, enAU } from 'date-fns/locale';

function DatePickerCommon({
  value,
  onChange,
  disabledDates,
  disabledDay,
  getMinimumDays,
  getDaysRequirement,
  // DatePicker props
  minDate = addDays(startOfToday(), 1),
  maxDate = addYears(startOfToday(), 2),
  months,
  monthDisplayFormat,
  direction,
  className,
  fixedHeight,
  scroll,
  showMonthArrow,
}) {
  const locale = useLocale();
  const t = useTranslations();
  const [ranges, setRanges] = useState([
    {
      startDate: value?.startDate
        ? new Date(value.startDate)
        : addDays(new Date(), 1),
      endDate: value?.endDate
        ? new Date(value.endDate)
        : addDays(new Date(), 2),
      key: 'selection',
    },
  ]);

  const handleChange = useCallback(
    ({ selection }) => {
      const startDate = selection.startDate;
      let endDate = selection.endDate;
      const isSame =
        startDate && endDate ? isSameDay(startDate, endDate) : false;
      const formattedStartDate = startDate && format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = endDate && format(endDate, 'yyyy-MM-dd');
      setRanges(
        startDate
          ? [
              {
                key: selection.key,
                startDate,
                endDate,
              },
            ]
          : []
      );

      onChange({
        startDate: formattedStartDate,
        endDate: isSame ? null : formattedEndDate,
      });
    },
    [onChange]
  );

  const minimumDays = useMemo(() => {
    if (!getMinimumDays) return [];
    return getMinimumDays(ranges);
  }, [getMinimumDays, ranges]);

  return (
    <DateRangePicker
      locale={locale === 'sv' ? sv : enAU}
      minDate={minDate}
      months={months}
      maxDate={maxDate}
      moveRangeOnFirstSelection={false}
      weekdayDisplayFormat="EEEEEE"
      showSelectionPreview={true}
      weekStartsOn={1}
      monthDisplayFormat={monthDisplayFormat}
      ranges={ranges}
      direction={direction}
      showDateDisplay={false}
      preventSnapRefocus={true}
      onChange={handleChange}
      className={className}
      disabledDates={disabledDates}
      disabledDay={disabledDay}
      dayContentRenderer={(d) => {
        const previous = addDays(d, -1);
        const requirementPrevious =
          getDaysRequirement &&
          !isSameDay(new Date(), previous) &&
          getDaysRequirement(previous);
        const requirementCurrent = getDaysRequirement && getDaysRequirement(d);
        const requirementSelected =
          getDaysRequirement &&
          ranges.length &&
          ranges[0].startDate &&
          getDaysRequirement(ranges[0].startDate);

        const isSelectable = isAfter(d, new Date());
        const isToday = isSameDay(new Date(), d);
        const isCheckoutOnly = requirementCurrent
          ? !requirementCurrent.availableForCheckin &&
            requirementPrevious?.availableForCheckin
          : false;
        const isMinimumDay = minimumDays.find((minDay) =>
          isSameDay(new Date(minDay), d)
        );
        const isCheckin =
          ranges.length && ranges[0].startDate && ranges[0].endDate
            ? isSameDay(d, ranges[0].startDate)
            : false;
        const isCheckout =
          ranges.length && ranges[0].startDate && ranges[0].endDate
            ? isSameDay(d, ranges[0].endDate)
            : false;
        const correctRange =
          getDaysRequirement &&
          requirementSelected &&
          ranges.length &&
          ranges[0].startDate &&
          ranges[0].endDate
            ? differenceInDays(ranges[0].endDate, ranges[0].startDate) >=
              (requirementSelected?.minimumNightStay || 1)
            : false;
        const isNotCorrectSelected =
          getDaysRequirement && ranges.length && !correctRange
            ? isSameDay(d, ranges[0].startDate) ||
              isSameDay(d, ranges[0].endDate)
            : false;

        return (
          <Day
            value={d.getDate()}
            enabledTooltip={
              (getDaysRequirement &&
                (isToday ||
                  isCheckoutOnly ||
                  isCheckin ||
                  isCheckout ||
                  (requirementSelected && isNotCorrectSelected))) ||
              isToday ||
              isCheckin ||
              isCheckout
            }
            tooltip={
              isToday
                ? t('date-range-tooltip-today')
                : requirementSelected && isNotCorrectSelected
                ? t(
                    `date-range-tooltip-${
                      requirementSelected?.minimumNightStay === 1
                        ? 'night'
                        : 'nights'
                    }`,
                    {
                      nights: requirementSelected?.minimumNightStay || 1,
                    }
                  )
                : isCheckin
                ? t('date-range-tooltip-checkin')
                : isCheckout
                ? t('date-range-tooltip-checkout')
                : isCheckoutOnly
                ? t('date-range-tooltip-checkout-only')
                : null
            }
            isMinimumDay={isMinimumDay}
            dataTestId={formatTestId(d)}
            className={
              isCheckoutOnly ? 'checkoutOnly' : isSelectable ? 'selectable' : ''
            }
          />
        );
      }}
      fixedHeight={fixedHeight}
      scroll={scroll}
      showMonthArrow={showMonthArrow}
    />
  );
}

const withScroll = {
  enabled: true,
  // HACK: react-date-range can't calculate necessary height for month.
  // It has special props where you can pass height values.
  // These values obtained via devtools.
  // Just remove fixed height in styles
  // of div.rdrMonth and see what height it is.
  monthHeight: 266,
  longMonthHeight: 310,
};

const UNKNOWN_MARGIN = 13;
const monthHeight = withScroll.longMonthHeight + UNKNOWN_MARGIN;

export function DatePickerModal({
  value,
  onChange,
  availableHeight,
  minDate,
  maxDate,
  disabledDates,
  disabledDay,
  getMinimumDays,
  getDaysRequirement,
}) {
  return (
    <DatePickerCommon
      value={value}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      disabledDates={disabledDates}
      disabledDay={disabledDay}
      getMinimumDays={getMinimumDays}
      getDaysRequirement={getDaysRequirement}
      months={availableHeight ? availableHeight / monthHeight : 1}
      monthDisplayFormat="MMMM yyyy"
      direction="vertical"
      className="daterange-mobile"
      fixedHeight={false}
      scroll={withScroll}
      showMonthArrow={false}
    />
  );
}

export function DatePickerRange({
  value,
  onChange,
  isFooterHidden,
  isBorderVisible,
  disabledDates,
  disabledDay,
  getMinimumDays,
  getDaysRequirement,
  minDate,
  maxDate,
  onSelectInexactDays,
  inexactStay,
}) {
  const isSecondMonthVisible = useMediaQuery({ query: '(min-width: 608px)' });
  return (
    <div>
      <DatePickerCommon
        value={value}
        onChange={onChange}
        disabledDates={disabledDates}
        disabledDay={disabledDay}
        getMinimumDays={getMinimumDays}
        getDaysRequirement={getDaysRequirement}
        minDate={minDate}
        maxDate={maxDate}
        months={isSecondMonthVisible ? 2 : 1}
        monthDisplayFormat="MMMM"
        direction="horizontal"
        className={isBorderVisible ? 'daterange-border' : ''}
      />

      {!isFooterHidden && (
        <InexactDaysButtons
          onSelect={onSelectInexactDays}
          value={inexactStay}
        />
      )}
    </div>
  );
}

function formatTestId(date) {
  return `date-${format(date, 'yyyy-MM-dd')}`;
}
